import { type FC, useMemo, useState } from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { Modal } from '../../../../components/Modal/Modal'
import { type SchedulingEventType } from '../../../../models/Scheduling'
import { PlaceChip } from '../../../Scheduling/rows/PlaceChip'
import { CoHostTag } from '../../../Scheduling/shared/CoHostTag'
import { isCoHostOf, isIndividualCoHostOf } from '../../../Scheduling/shared/eventTypeHostState'

const PAGE_SIZE = 5

interface AddSharedEventTypesModalProps {
  eventTypes: SchedulingEventType[]
  isLoading?: boolean
  onCancel: () => void
  onAdd: (eventTypes: SchedulingEventType[]) => void
}

export const AddSharedEventTypesModal: FC<AddSharedEventTypesModalProps> = ({ eventTypes, isLoading = false, onCancel, onAdd }) => {
  const [query, setQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [page, setPage] = useState(0)

  const filtered = useMemo(() => eventTypes.filter((eventType) => eventType.name.toLowerCase().includes(query.trim().toLowerCase())), [eventTypes, query])
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount - 1)
  const visible = filtered.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE)
  const selectable = filtered.filter((eventType) => eventType.active)
  const allSelected = selectable.length > 0 && selectable.every((eventType) => selectedIds.includes(eventType.id))

  const reset = () => {
    setQuery('')
    setSelectedIds([])
    setPage(0)
  }

  const handleCancel = () => {
    reset()
    onCancel()
  }

  const handleAdd = () => {
    onAdd(eventTypes.filter((eventType) => eventType.active && selectedIds.includes(eventType.id)))
    reset()
  }

  const toggle = (id: string) => setSelectedIds((ids) => (ids.includes(id) ? ids.filter((current) => current !== id) : [...ids, id]))
  const toggleAll = () => setSelectedIds(allSelected ? [] : selectable.map((eventType) => eventType.id))

  return (
    <Modal
      show
      onHide={handleCancel}
      size='lg'
      header={
        <div className='d-flex flex-column'>
          <h5 className='mb-0 d-flex align-items-center zocks-gap-0.5'>
            <i className='bi bi-people' />
            Add shared event types from your account
          </h5>
          <span className='paragraph-small zocks-text-gray-600'>You can customize them later.</span>
        </div>
      }
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-3'>
          <InputGroup>
            <InputGroup.Text className='zocks-bg-color-white'>
              <i className='bi bi-search zocks-text-gray-500' />
            </InputGroup.Text>
            <Form.Control
              type='search'
              placeholder='Search'
              aria-label='Search event types'
              value={query}
              onChange={(event) => {
                setQuery(event.currentTarget.value)
                setPage(0)
              }}
            />
          </InputGroup>
          <div className='d-flex align-items-center justify-content-between py-2 zocks-px-1.25 rounded-2 zocks-bg-color-gray-100'>
            <span className='paragraph-small zocks-text-gray-600'>
              Showing {visible.length} out of {filtered.length}
            </span>
            <div className='d-flex gap-1'>
              <Button variant='grayGhost' className='p-1' aria-label='Previous page' disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                <i className='bi bi-chevron-left' />
              </Button>
              <Button
                variant='grayGhost'
                className='p-1'
                aria-label='Next page'
                disabled={currentPage >= pageCount - 1}
                onClick={() => setPage(currentPage + 1)}
              >
                <i className='bi bi-chevron-right' />
              </Button>
            </div>
          </div>
          <div className='d-flex flex-column'>
            {visible.map((eventType) => (
              <label
                key={eventType.id}
                htmlFor={`add-shared-${eventType.id}`}
                className='d-flex align-items-start gap-3 py-3 border-top zocks-border-color-gray-200'
              >
                <Form.Check
                  id={`add-shared-${eventType.id}`}
                  type='checkbox'
                  className='mt-1'
                  checked={selectedIds.includes(eventType.id)}
                  disabled={!eventType.active && !selectedIds.includes(eventType.id)}
                  onChange={() => toggle(eventType.id)}
                />
                <div className='d-flex flex-column flex-grow-1 zocks-gap-0.25'>
                  <span className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
                    <span className='bold'>{eventType.name}</span>
                    <span className='zocks-text-gray-600'>·</span>
                    <span className='paragraph-small zocks-text-gray-600'>{eventType.durationMinutes} min</span>
                    <PlaceChip meetingMode={eventType.meetingMode} />
                    {isCoHostOf(eventType) ? <CoHostTag label='You’re a co-host' /> : null}
                  </span>
                  {eventType.clientFacingText ? <span className='paragraph-small zocks-text-gray-600'>{eventType.clientFacingText}</span> : null}
                  {isIndividualCoHostOf(eventType) ? (
                    <span className='paragraph-small zocks-text-gray-600'>
                      You’re a co-host here. On your own page you’ll be the owner, so you won’t be added twice.
                    </span>
                  ) : null}
                </div>
                {eventType.active ? (
                  <span className='badge rounded-pill zocks-bg-color-success-background zocks-text-success'>Active</span>
                ) : (
                  <span className='badge rounded-pill zocks-bg-color-gray-200 zocks-text-gray-600'>Inactive</span>
                )}
              </label>
            ))}
          </div>
        </div>
      }
      footerClassName='justify-content-between align-items-center'
      footer={
        <div className='d-flex align-items-center zocks-gap-0.5'>
          <button type='button' className='btn btn-link p-0 paragraph-small' disabled={selectable.length === 0} onClick={toggleAll}>
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
          <span className='zocks-text-gray-400'>&middot;</span>
          <span className='paragraph-small zocks-text-gray-600'>{selectedIds.length} selected</span>
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle='Add event types'
      footerPrimaryActionDisabled={selectedIds.length === 0}
      footerPrimaryActionLoading={isLoading}
      footerSecondaryActionTitle='Cancel'
      onFooterPrimaryAction={handleAdd}
      onFooterSecondaryAction={handleCancel}
    />
  )
}
